import { Action } from '../view/types';
import { DocumentModel } from '../document/types';
import { ContractStatus } from '../contract/types';

export const accountingFeatureKey = 'accounting';

export type AccountModel = {
  id: number;
  contactId?: number;
  contactName: string;
  accountType: AccountType;
  accountTypeLoc: string;
  name: string;
  code: string;
  active: boolean;
  canHandleCash: boolean;
};

export type AccountMoveTabDetailModel = {
  id: number;
  name: string;
  contractName?: string;
  creditAccountName: string;
  clientAccountName: string;
  companyOrBrokerAccountName: string;
  reference: string;
  creationDate: Date;
  periodStartDate: Date;
  periodEndDate: Date;
  period: string;
  issuingAccountMoves: AccountMoveLightModel[];
  payingAccountMoves: AccountMoveLightModel[];
  repaymentAccountMoves: AccountMoveLightModel[];
  accountMoveLines: AccountMoveLineTabDetailModel[];
  isReadOnly: boolean;
  isAdminOnly: boolean;
  contractId?: number;
  contractStatusId?: number;
  paymentStatus: PaymentStatus;
  paymentStatusLoc: string;
  status: AccountMoveStatus;
  statusLoc: string;
  moveType: MoveType;
  actions: Action[];
};

export type AccountMoveLineTabDetailModel = {
  id: number;
  unitPrice: number;
  paymentModeShortNameLoc?: string;
  creationDate: Date;
  paymentMode: PaymentMode | null;
  paymentModeLoc: string;
  documentId?: number;
  unitPriceClientView?: number;
};

export type AccountMoveModel = {
  id: number;
  contactId?: number | null;
  currencyId: number;
  salesPersonId: number;
  creditAccountId: number;
  creditAccountName: string;
  debitAccountId: number;
  debitAccountName: string;
  clientAccountName?: string;
  companyOrBrokerAccountName?: string;
  contractId?: number | null;
  contractUniqueId?: string;
  contractName?: string;
  insuranceClaimId?: number;
  paymentStatus: PaymentStatus;
  paymentStatusLoc: string;
  name: string;
  reference: string;
  directionSign: number;
  status: AccountMoveStatus;
  statusLoc: string;
  moveType: MoveType;
  moveTypeLoc: string;
  paymentReference: string;
  autoPost: AutoPost;
  deliveryDate: Date;
  creationDate: Date;
  accountMoveLines: AccountMoveLineModel[];
  periodEndDate: Date;
  periodStartDate: Date;
  period: string;
  journalId?: number;
  journalName: string;
  issuingAccountMovesCount: number;
  payingAccountMovesCount: number;
  issuingAccountMoves: AccountMoveModel[];
  payingAccountMoves: AccountMoveModel[];
  repaymentAccountMoves: AccountMoveModel[];
  actions: Action[];
  accountMoveTypeId: number;
  isReadOnly: boolean;
  isAdminOnly: boolean;
};

export type AccountMoveLineModel = {
  id: number;
  accountMoveId: number;
  currencyId: number;
  contactId?: number;
  name: string;
  description: string;
  sequence: number;
  quantity: number;
  unitPrice: number;
  discount: number;
  debit: number;
  credit: number;
  paymentMode: PaymentMode | null;
  paymentModeLoc: string;
  paymentModeShortNameLoc?: string;
  paymentStatus: PaymentStatus | null;
  paymentStatusLoc: string;
  creationDate: Date;
  documentId?: number;
  proofFile?: File;
  unitPriceClientView?: number;
};

export type IssuingAccountMovePayingAccountMoveModel = {
  issuingAccountMoves: number[];
  payingAccountMoves: number[];
  bindType: BindType;
};

export type AccountMoveInstanceParams = {
  moveType?: MoveType;
  contractId?: number;
  insuranceClaimId?: number;
};

export type AccountMovePeriodModel = {
  name: string;
  startDate: Date;
  endDate: Date;
};

export type AccountMoveLightModel = {
  id: number;
  contractId: number;
  contractUniqueId: string;
  debitAccountId: number;
  parentId: number;
  creditAccountId: number;
  name: string;
  reference: string;
  productInsuranceCompanyName: string;
  accountMoveTypeId: number;
  moveType: MoveType;
  moveTypeLoc: string;
  status: AccountMoveStatus;
  paymentReference: string;
  journalId?: number;
  journalName: string;
  periodStartDate: Date;
  periodEndDate: Date;
  accountMoveLines: AccountMoveLineModel[];
  totalAmount: number;
};

export type SlipModel = {
  debitAccountId: number;
  creditAccountId: number;
  reference: string;
  file: File;
};

export type SlipRecordModel = {
  contractName: string;
  accountMoveName: string;
  accountMoveType: AccountMoveType;
  amount: number;
  startDate?: Date;
  endDate?: Date;
};

export type SlipResponseModel = {
  slipRecordModel: SlipRecordModel;
  status: SlipStatus;
  message: string;
};

export type JournalAssignRecord = {
  id: number;
  name: string;
  amount: number;
  creationDate: Date;
};

export type CashJournalModel = {
  balance: number;
  accountId: number;
  accountName: string;
  accountContactId?: number;
  cashJournalEntries: CashJournalEntry[];
};

export type CashJournalEntry = {
  id: number;
  name: string;
  contactId?: number;
  salesPersonId: number;
  salesPersonName: string;
  documentId: number;
  debitAccountId: number;
  debitAccountName: string;
  creditAccountId: number;
  creditAccountName: string;
  displayAccountName: string;
  salesTeamName: string;
  reference: string;
  description: string;
  displayDescription: string;
  amount: number;
  directionSign: number;
  creationDate: Date;
  accountMoveTypeFlow: AccountMoveTypeFlow;
  accountMoveTypeFlowLoc: string;
  document: DocumentModel;
  actions: Action[];
};

export type PaymentEntryModel = {
  accountMoveId: number;
  accountMoveDebitAccountName: string;
  paymentMode?: PaymentMode;
  accountMoveReference: string;
  amount: number;
  creationDate: Date;
  accountMoveStatus: AccountMoveStatus;
};

export type InvoiceFormModel = {
  invoiceName: string;
  accountMoveTypeId?: number;
  debitAccountId: number;
  creditAccountId: number;
  invoiceAmount: number;
  invoiceNumber: string;
  periodStartDate: Date;
  periodEndDate: Date;
};

export type PaymentFormModel = {
  paymentMode: PaymentMode;
  paymentDate: Date;
  reference: string;
  proofFile?: File;
};

export type InvoiceExtendedFormModel = {
  invoiceFormModel: InvoiceFormModel;
  paymentFormModel: PaymentFormModel | null;
  needToCreateRepayment: boolean;
  contractId: number;
  insuranceClaimId?: number;
};

/**
 * Entité qui porte l'onglet des quittances (`AccountMovesTabComponent`).
 */
export type AccountMoveOwnerType = 'Contract' | 'InsuranceClaim';

/**
 * Ancre de l'onglet des quittances.
 *
 * `contractId` est toujours renseigné : c'est l'ancre comptable, y compris en contexte sinistre
 * (`InsuranceClaim.ContractId` est non-nullable côté backend). `insuranceClaimId` est un raffinement
 * de cette ancre, jamais une ancre alternative.
 *
 * `insuranceCompanyContactId` est optionnel : la compagnie vient de la ligne de produit du contrat,
 * qui est nullable côté backend. Les modales doivent donc tolérer son absence.
 */
export type AccountMoveOwnerContext = {
  ownerType: AccountMoveOwnerType;
  contractId: number;
  contactId: number;
  insuranceCompanyContactId?: number;
  insuranceClaimId?: number;
};

/**
 * Visibilité des boutons et colonnes de l'onglet des quittances. Tout champ omis retombe sur le défaut
 * dérivé de `isClient`, qui reproduit l'affichage historique de l'onglet contrat.
 */
export type AccountMoveTabOptions = {
  showAddInvoice?: boolean;
  showBindPayment?: boolean;
  showBindRepayment?: boolean;
  showSlipColumn?: boolean;
  showPeriodColumn?: boolean;
};

export type AccountMoveStatus = 'Draft' | 'Posted' | 'Cancel';
export type MoveType = 'Entry' | 'CustomerInvoice' | 'VendorInvoice';
export type PaymentMode =
  | 'Cash'
  | 'Card'
  | 'BankTransfert'
  | 'Cheque'
  | 'DirectDebit';
export type PaymentStatus =
  | 'NotPaid'
  | 'InPayment'
  | 'Paid'
  | 'PartiallyPaid'
  | 'Reversed';
export type AutoPost = 'No' | 'AtDate' | 'Monthly' | 'Quartly' | 'Yearly';
export type AccountType = 'Client' | 'Company' | 'SalesTeam' | 'Bank';
export type BindType = 'Payment' | 'Repayment' | 'Slip';
export type AccountMoveType =
  | 'RecurringFees'
  | 'ApplicationFees'
  | 'LitigationFees'
  | 'OverPayment';
export type SlipStatus = 'Ok' | 'Error';
export type PaymentType =
  | 'CustomerReimbursement' //Remboursement client
  | 'CustomerPayment' //Paiement client
  | `CashOut` //Retrait d'espèces
  | 'InitialAmount' //Montant initial
  | 'CashIn'; //Dépôt d'espèces
export type AccountMoveTypeFlow =
  | 'None'
  | 'Cash'
  | 'CashIn'
  | 'CashOut'
  | 'CustomerPayment'
  | 'CustomerReimbursement'
  | 'InitialBalance';

export enum AccountMoveInvoiceExtendedRepaymentChoice {
  NoRepayment = 1,
  WithRepaymentBroker = 2,
  WithRepaymentCompany = 3,
}
export const RECURRING_FEES_ID: number = 1000000;
export const APPLICATION_FEES_ID: number = 1000001;
export const INSURANCE_CLAIM_FEES_ID: number = 1000003;
export const SALES_PERSON_COMMISSION_ID: number = 1000002;
